import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { api } from '../../utils/api';
import { formatCurrency, formatPercent, getMarginStatus } from '../../utils/formatters';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  AlertCircle,
  Save,
  X,
  MapPin,
  Building2,
  MapPinned,
  Layers,
  ChevronDown,
  ChevronRight,
  Landmark,
  Building,
  Users,
  TreePine,
  Home,
  Search,
  Zap,
  Send
} from 'lucide-react';
import { format } from 'date-fns';

// Confirmation Modal Component - replaces browser confirm() which Safari blocks
function ConfirmModal({ isOpen, title, message, confirmText, confirmStyle, onConfirm, onCancel }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <div className="p-4">
          <p className="text-gray-600">{message}</p>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button onClick={onCancel} className="btn-secondary">
            Go Back
          </button>
          <button 
            onClick={onConfirm} 
            className={confirmStyle === 'danger' ? 'btn-primary bg-red-600 hover:bg-red-700' : 'btn-primary'}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Category definitions - must match backend enum
const CATEGORIES = [
  { value: 'CASINO', label: 'Casinos & Resorts', icon: Landmark, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  { value: 'CORPORATE', label: 'Corporate', icon: Building, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { value: 'PROMOTER', label: 'Promoters & Agencies', icon: Users, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { value: 'MUNICIPAL', label: 'Municipal / Government', icon: TreePine, color: 'text-green-600', bgColor: 'bg-green-50' },
  { value: 'PRIVATE', label: 'Private Events', icon: Home, color: 'text-rose-600', bgColor: 'bg-rose-50' },
];

const getCategoryInfo = (value) => CATEGORIES.find(c => c.value === value) || CATEGORIES[0];

// Categorized Location Dropdown Component
function LocationDropdown({ 
  value, 
  onChange, 
  hierarchy, 
  onAddClient, 
  onAddVenue, 
  onAddLocation 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({ CASINO: true });
  const [expandedClients, setExpandedClients] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clientsByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = hierarchy.filter(c => (c.category || 'CASINO') === cat.value);
    return acc;
  }, {});

  const getSelectedDisplay = () => {
    if (!value) return 'Select location...';
    for (const client of hierarchy) {
      for (const venue of client.venues) {
        for (const loc of venue.locations) {
          if (loc.id === value) {
            return `${client.name} → ${venue.name} → ${loc.name}`;
          }
        }
      }
    }
    return 'Select location...';
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const toggleClient = (clientId) => {
    setExpandedClients(prev => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  const selectLocation = (locId) => {
    onChange(locId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const filterHierarchy = (clients) => {
    if (!searchTerm) return clients;
    const term = searchTerm.toLowerCase();
    return clients.filter(client => {
      const clientMatch = client.name.toLowerCase().includes(term);
      const venueMatch = client.venues.some(v => 
        v.name.toLowerCase().includes(term) ||
        v.locations.some(l => l.name.toLowerCase().includes(term))
      );
      return clientMatch || venueMatch;
    });
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="input w-full text-left flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {getSelectedDisplay()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search clients, venues, locations..."
                className="input pl-9 py-2 text-sm"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-72">
            {CATEGORIES.map(cat => {
              const clients = filterHierarchy(clientsByCategory[cat.value] || []);
              if (clients.length === 0 && !searchTerm) return null;
              
              const Icon = cat.icon;
              const isExpanded = expandedCategories[cat.value];

              return (
                <div key={cat.value} className="border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.value)}
                    className={`w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 ${cat.bgColor}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${cat.color}`} />
                      <span className="font-medium text-sm">{cat.label}</span>
                      <span className="text-xs text-gray-500">({clients.length})</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="bg-white">
                      {clients.length === 0 ? (
                        <p className="px-4 py-2 text-sm text-gray-400 italic">No clients in this category</p>
                      ) : (
                        clients.map(client => {
                          const isClientExpanded = expandedClients[client.id];
                          return (
                            <div key={client.id}>
                              <button
                                type="button"
                                onClick={() => toggleClient(client.id)}
                                className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium text-sm">{client.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onAddVenue(client); }}
                                    className="text-xs text-hub-600 hover:text-hub-800"
                                  >
                                    + Venue
                                  </button>
                                  {isClientExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>
                              </button>

                              {isClientExpanded && (
                                <div className="bg-gray-50">
                                  {client.venues.length === 0 ? (
                                    <p className="px-6 py-2 text-sm text-gray-400 italic">No venues</p>
                                  ) : (
                                    client.venues.map(venue => (
                                      <div key={venue.id}>
                                        <div className="px-6 py-1.5 flex items-center justify-between text-sm text-gray-600 bg-gray-100">
                                          <div className="flex items-center gap-2">
                                            <MapPinned className="w-3 h-3" />
                                            <span>{venue.name}</span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); onAddLocation(venue, client); }}
                                            className="text-xs text-hub-600 hover:text-hub-800"
                                          >
                                            + Location
                                          </button>
                                        </div>
                                        {venue.locations.map(loc => (
                                          <button
                                            key={loc.id}
                                            type="button"
                                            onClick={() => selectLocation(loc.id)}
                                            className={`w-full px-8 py-2 text-left text-sm hover:bg-hub-50 flex items-center gap-2 ${value === loc.id ? 'bg-hub-100 text-hub-700' : ''}`}
                                          >
                                            <Layers className="w-3 h-3 text-gray-400" />
                                            {loc.name}
                                          </button>
                                        ))}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                      <button
                        type="button"
                        onClick={onAddClient}
                        className="w-full px-4 py-2 text-sm text-hub-600 hover:bg-hub-50 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add New Client
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Modal Component
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function AddClientModal({ isOpen, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('CASINO');
  const [billingAddress, setBillingAddress] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const client = await api.post('/clients', { name, category, billingAddress });
      onCreated(client);
      setName('');
      setCategory('CASINO');
      setBillingAddress('');
      onClose();
    } catch (err) {
      alert(err.message);
    }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Client">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Client Name *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g., MGM Resorts" required />
        </div>
        <div>
          <label className="label">Category *</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="input">
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Billing Address</label>
          <textarea value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} className="input" placeholder="Full billing address" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Client'}</button>
        </div>
      </form>
    </Modal>
  );
}

function AddVenueModal({ isOpen, onClose, onCreated, client }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client?.billingAddress) {
      setAddress(client.billingAddress);
    }
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const venue = await api.post('/venues', { clientId: client.id, name, address });
      onCreated(venue);
      setName('');
      setAddress('');
      onClose();
    } catch (err) {
      alert(err.message);
    }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add Venue to ${client?.name || 'Client'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Venue Name *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g., Bellagio" required />
        </div>
        <div>
          <label className="label">Address</label>
          <textarea value={address} onChange={(e) => setAddress(e.target.value)} className="input" placeholder="Full venue address" />
          <p className="text-xs text-gray-500 mt-1">Pre-filled from client billing address</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Venue'}</button>
        </div>
      </form>
    </Modal>
  );
}

function AddLocationModal({ isOpen, onClose, onCreated, venue }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const location = await api.post('/locations', { venueId: venue.id, name });
      onCreated(location);
      setName('');
      onClose();
    } catch (err) {
      alert(err.message);
    }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add Location to ${venue?.name || 'Venue'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Location Name *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g., Baccarat Bar, Pool Deck" required />
        </div>
        <p className="text-sm text-gray-500">Address will inherit from venue: {venue?.address || '(no address set)'}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Location'}</button>
        </div>
      </form>
    </Modal>
  );
}

function AssignmentRow({ assignment, contractors, roles, onChange, onRemove, onForceAccept }) {
  const canForceAccept = assignment.id && assignment.status === 'PENDING';
  
  return (
    <div className="p-3 bg-gray-50 rounded-lg space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="label">Contractor</label>
            <select value={assignment.contractorId} onChange={(e) => onChange({ ...assignment, contractorId: e.target.value })} className="input" required>
              <option value="">Select...</option>
              {contractors.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="label">Role</label>
            <select value={assignment.role || ''} onChange={(e) => onChange({ ...assignment, role: e.target.value })} className="input">
              <option value="">Select role...</option>
              {roles.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          <div>
            <label className="label">Payout</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input type="number" value={assignment.payout} onChange={(e) => onChange({ ...assignment, payout: e.target.value })} className="input pl-7" placeholder="0" min="0" step="1" required />
            </div>
          </div>
          <div>
            <label className="label">Status</label>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-2 rounded-lg text-sm ${
                assignment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                assignment.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' :
                assignment.status === 'DECLINED' ? 'bg-red-100 text-red-800' :
                assignment.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              }`}>{assignment.status || 'PENDING'}</span>
            </div>
            <div className="flex gap-1 mt-2">
              {canForceAccept && (<button type="button" onClick={() => onForceAccept(assignment.id)} className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700">Force Accept</button>)}
            </div>
          </div>
        </div>
        <button type="button" onClick={onRemove} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
      </div>
      {assignment.contractorNotes && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-medium text-blue-700 mb-1">Contractor Notes:</p>
          <p className="text-sm text-blue-900">{assignment.contractorNotes}</p>
        </div>
      )}
    </div>
  );
}

export default function AdminGigEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id;
  const returnState = location.state;

  const goBack = () => {
    if (returnState?.returnTo) {
      navigate(returnState.returnTo, { state: { view: returnState.view, previousView: returnState.previousView, currentDate: returnState.currentDate, selectedDate: returnState.selectedDate } });
    } else {
      navigate(-1);
    }
  };

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(searchParams.get('date') || format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('20:00');
  const [endTime, setEndTime] = useState('00:00');
  const [clientBilled, setClientBilled] = useState('');
  const [expenses, setExpenses] = useState('0');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState('PENDING');
  const [assignments, setAssignments] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showForceAcceptConfirm, setShowForceAcceptConfirm] = useState(false);
  const [pendingForceAcceptId, setPendingForceAcceptId] = useState(null);
  const [selectedClientForVenue, setSelectedClientForVenue] = useState(null);
  const [selectedVenueForLocation, setSelectedVenueForLocation] = useState(null);
  const [templates, setTemplates] = useState([]);
  
  // Track original state for change detection
  const [originalState, setOriginalState] = useState(null);

  const totalPayout = assignments.reduce((sum, a) => sum + (parseFloat(a.payout) || 0), 0);
  const billed = parseFloat(clientBilled) || 0;
  const exp = parseFloat(expenses) || 0;
  const net = billed - exp;
  const productionFee = net - totalPayout;
  const marginPercent = net > 0 ? ((productionFee / net) * 100).toFixed(1) : 0;

  const loadHierarchy = () => api.get('/locations/hierarchy').then(setHierarchy);

  useEffect(() => {
    Promise.all([loadHierarchy(), api.get('/contractors?active=true'), api.get('/roles'), api.get('/templates')]).then(([_, c, r, t]) => { 
      setContractors(c); 
      setRoles(r);
      setTemplates(t || []);
      // Pre-populate contractor from URL if provided (from Scheduler empty cell click)
      const contractorId = searchParams.get('contractorId');
      if (contractorId && isNew && assignments.length === 0) {
        setAssignments([{ contractorId, role: '', payout: '', status: 'PENDING' }]);
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!isNew) {
      api.get(`/gigs/${id}`).then((gig) => {
        setLocationId(gig.locationId);
        setDate(gig.date.substring(0, 10));
        setStartTime(gig.startTime);
        setEndTime(gig.endTime);
        setClientBilled(gig.clientBilled);
        setExpenses(gig.expenses || '0');
        setNotes(gig.notes || '');
        setAddress(gig.address || '');
        setStatus(gig.status);
        const loadedAssignments = gig.assignments.map(a => ({ 
          id: a.id, 
          contractorId: a.contractorId, 
          role: a.role, 
          payout: a.payout, 
          status: a.status, 
          contractorNotes: a.contractorNotes 
        }));
        setAssignments(loadedAssignments);
        
        // Store original state for change detection (exclude clientBilled - internal only)
        setOriginalState({
          locationId: gig.locationId,
          date: gig.date.substring(0, 10),
          startTime: gig.startTime,
          endTime: gig.endTime,
          notes: gig.notes || '',
          address: gig.address || '',
          assignments: gig.assignments.map(a => ({ 
            contractorId: a.contractorId, 
            role: a.role || '', 
            payout: parseFloat(a.payout) || 0 
          }))
        });
      }).catch(err => setError(err.message)).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  // Detect if any changes have been made (for published gigs only)
  const hasChanges = useMemo(() => {
    // No change detection for new gigs or drafts
    if (!originalState || isNew || status === 'DRAFT') return false;
    
    // Compare gig details (everything except clientBilled)
    if (locationId !== originalState.locationId) return true;
    if (date !== originalState.date) return true;
    if (startTime !== originalState.startTime) return true;
    if (endTime !== originalState.endTime) return true;
    if ((notes || '') !== originalState.notes) return true;
    if ((address || '') !== originalState.address) return true;
    
    // Compare assignments
    const currentAssignments = assignments.filter(a => a.contractorId);
    if (currentAssignments.length !== originalState.assignments.length) return true;
    
    // Check each assignment
    for (const curr of currentAssignments) {
      const orig = originalState.assignments.find(a => a.contractorId === curr.contractorId);
      if (!orig) return true; // New contractor added
      if ((curr.role || '') !== (orig.role || '')) return true;
      if ((parseFloat(curr.payout) || 0) !== (orig.payout || 0)) return true;
    }
    
    // Check for removed contractors
    for (const orig of originalState.assignments) {
      const curr = currentAssignments.find(a => a.contractorId === orig.contractorId);
      if (!curr) return true; // Contractor was removed
    }
    
    return false;
  }, [originalState, isNew, status, locationId, date, startTime, endTime, notes, address, assignments]);

  // Apply template to fill form fields
  const handleApplyTemplate = (templateId) => {
    if (!templateId) return;
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Fill all the fields from template
    setLocationId(template.locationId || '');
    setStartTime(template.startTime || '20:00');
    setEndTime(template.endTime || '00:00');
    setClientBilled(template.clientBilled?.toString() || '');
    setExpenses(template.expenses?.toString() || '0');
    setNotes(template.notes || '');

    // Set address from template's location
    if (template.locationId && hierarchy.length > 0) {
      for (const client of hierarchy) {
        for (const venue of client.venues) {
          for (const loc of venue.locations) {
            if (loc.id === template.locationId) {
              setAddress(loc.address || venue.address || client.billingAddress || '');
              break;
            }
          }
        }
      }
    }

    // Set contractor assignment with template defaults (role and payout)
    // Templates use defaultPayout and defaultRole field names
    // PRESERVE existing contractor if one is already selected
    if (template.defaultPayout || template.defaultRole) {
      if (assignments.length > 0 && assignments[0].contractorId) {
        // Preserve selected contractor, apply template defaults
        const newAssignments = [...assignments];
        newAssignments[0] = {
          ...newAssignments[0],
          payout: template.defaultPayout?.toString() || newAssignments[0].payout,
          role: template.defaultRole || newAssignments[0].role
        };
        setAssignments(newAssignments);
      } else {
        // No contractor selected, create placeholder with template defaults
        setAssignments([{
          contractorId: '',
          role: template.defaultRole || '',
          payout: template.defaultPayout?.toString() || '',
          status: 'PENDING'
        }]);
      }
    }
  };

  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    
    // Auto-fill address from location hierarchy
    if (newLocationId && hierarchy.length > 0) {
      for (const client of hierarchy) {
        for (const venue of client.venues) {
          for (const loc of venue.locations) {
            if (loc.id === newLocationId) {
              // Priority: location.address > venue.address > client.billingAddress
              setAddress(loc.address || venue.address || client.billingAddress || '');
              
              // Also set default billing/payout if new gig
              if (isNew) {
                if (loc.defaultClientBilled && !clientBilled) {
                  setClientBilled(loc.defaultClientBilled.toString());
                }
                if (loc.defaultPayout) {
                  // If there's a contractor already assigned, update their payout
                  // Otherwise, we'll use it when they add a contractor
                  if (assignments.length > 0 && !assignments[0].payout) {
                    const newAssignments = [...assignments];
                    newAssignments[0] = { ...newAssignments[0], payout: loc.defaultPayout.toString() };
                    setAssignments(newAssignments);
                  }
                }
              }
              return;
            }
          }
        }
      }
    }
  };

  const openAddVenue = (client) => { setSelectedClientForVenue(client); setShowAddVenue(true); };
  const openAddLocation = (venue, client) => { setSelectedVenueForLocation(venue); setSelectedClientForVenue(client); setShowAddLocation(true); };
  const handleClientCreated = async () => { await loadHierarchy(); };
  const handleVenueCreated = async () => { await loadHierarchy(); };
  const handleLocationCreated = async (loc) => { await loadHierarchy(); setLocationId(loc.id); handleLocationChange(loc.id); };
  
  const handleAddAssignment = () => { 
    // Get default payout from selected location if available
    let defaultPayout = '';
    if (locationId && hierarchy.length > 0) {
      for (const client of hierarchy) {
        for (const venue of client.venues) {
          for (const loc of venue.locations) {
            if (loc.id === locationId && loc.defaultPayout) {
              defaultPayout = loc.defaultPayout.toString();
              break;
            }
          }
        }
      }
    }
    setAssignments([...assignments, { contractorId: '', role: '', payout: defaultPayout, status: 'PENDING' }]); 
  };
  const handleUpdateAssignment = (index, updated) => { const newAssignments = [...assignments]; newAssignments[index] = updated; setAssignments(newAssignments); };
  const handleRemoveAssignment = (index) => { setAssignments(assignments.filter((_, i) => i !== index)); };

  const handleForceAccept = async (assignmentId) => {
    setPendingForceAcceptId(assignmentId);
    setShowForceAcceptConfirm(true);
  };
  
  const confirmForceAccept = async () => {
    if (!pendingForceAcceptId) return;
    setShowForceAcceptConfirm(false);
    try {
      await api.put(`/assignments/${pendingForceAcceptId}/force-accept`);
      const gig = await api.get(`/gigs/${id}`);
      setAssignments(gig.assignments.map(a => ({ id: a.id, contractorId: a.contractorId, role: a.role, payout: a.payout, status: a.status, contractorNotes: a.contractorNotes })));
    } catch (err) { alert(err.message); }
    setPendingForceAcceptId(null);
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Determine status:
      // - saveAsDraft = true → DRAFT
      // - saveAsDraft = false + new or draft → PENDING (publish it)
      // - saveAsDraft = false + already published → keep current status
      let newStatus;
      if (saveAsDraft) {
        newStatus = 'DRAFT';
      } else if (isNew || status === 'DRAFT') {
        newStatus = 'PENDING'; // Publishing
      } else {
        newStatus = status; // Keep current status for already-published gigs
      }

      const data = { 
        locationId, 
        date, 
        startTime, 
        endTime, 
        clientBilled: parseFloat(clientBilled), 
        expenses: parseFloat(expenses) || 0, 
        notes, 
        address, 
        status: newStatus,
        assignments: assignments.filter(a => a.contractorId).map(a => ({ 
          id: a.id, 
          contractorId: a.contractorId, 
          role: a.role, 
          payout: parseFloat(a.payout) || 0 
        })),
        // Signal to backend:
        // - For draft -> publish: need publishChanges=true to allow status change
        // - For published gig with changes: need publishChanges=true to send notifications
        publishChanges: !saveAsDraft && (status === 'DRAFT' || hasChanges)
      };
      if (isNew) { await api.post('/gigs', data); } else { await api.put(`/gigs/${id}`, data); }
      goBack();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  // No longer needed - handleSubmit handles publishing now
  const handlePublishDraft = async () => {
    if (!confirm('Publish this draft? Contractors will be notified.')) return;
    setSaving(true);
    try {
      await api.put(`/gigs/${id}/publish`);
      goBack();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    setShowCancelConfirm(true);
  };
  
  const confirmCancel = async () => {
    setShowCancelConfirm(false);
    try { await api.put(`/gigs/${id}/cancel`); goBack(); } catch (err) { setError(err.message); }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try { await api.delete(`/gigs/${id}`); goBack(); } catch (err) { setError(err.message); }
  };

  const handleSaveAsTemplate = async () => {
    // Get location name for suggested template name
    let locName = 'Gig';
    for (const client of hierarchy) {
      for (const venue of client.venues) {
        for (const loc of venue.locations) {
          if (loc.id === locationId) {
            locName = loc.name;
            break;
          }
        }
      }
    }
    
    // Format times nicely
    const formatTime = (t) => new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const suggestedName = `${locName} ${formatTime(startTime)}-${formatTime(endTime)}`;
    
    const name = prompt('Template name:', suggestedName);
    if (!name) return;
    
    try {
      await api.post(`/templates/from-gig/${id}`, { name });
      alert('Template saved!');
    } catch (err) {
      alert(err.message);
    }
  };

  // Determine button text based on state
  const getSubmitButtonText = () => {
    if (saving) return 'Saving...';
    if (isNew) return 'Publish Gig';
    if (status === 'DRAFT') return 'Publish Gig';
    if (hasChanges) return 'Publish Changes';
    return 'Save Gig';
  };

  const getSubmitButtonIcon = () => {
    if (hasChanges && status !== 'DRAFT' && !isNew) {
      return <Send className="w-4 h-4" />;
    }
    return <Save className="w-4 h-4" />;
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      <AddClientModal isOpen={showAddClient} onClose={() => setShowAddClient(false)} onCreated={handleClientCreated} />
      <AddVenueModal isOpen={showAddVenue} onClose={() => setShowAddVenue(false)} onCreated={handleVenueCreated} client={selectedClientForVenue} />
      <AddLocationModal isOpen={showAddLocation} onClose={() => setShowAddLocation(false)} onCreated={handleLocationCreated} venue={selectedVenueForLocation} />

      <div className="flex items-center gap-4 mb-6">
        <button onClick={goBack} className="btn-ghost p-2"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'New Gig' : 'Edit Gig'}</h1>
        <div className="ml-auto flex items-center gap-2">
          {!isNew && locationId && (
            <button type="button" onClick={handleSaveAsTemplate} className="btn-ghost text-hub-600 hover:bg-hub-50">
              <Zap className="w-4 h-4 mr-1" />Save as Template
            </button>
          )}
          {!isNew && status !== 'CANCELLED' && (
            <button type="button" onClick={handleCancel} className="btn-ghost text-orange-600 hover:bg-orange-50">
              <X className="w-4 h-4 mr-1" />Cancel Gig
            </button>
          )}
          {!isNew && (
            <button type="button" onClick={handleDelete} className="btn-ghost text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" />Delete Gig
            </button>
          )}
        </div>
      </div>

      {error && (<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"><AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" /><div className="text-red-600">{error}</div></div>)}

      {status === 'DRAFT' && (
        <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
          <div>
            <p className="font-medium text-yellow-800">This is a Draft</p>
            <p className="text-sm text-yellow-700">Contractors have not been notified. Publish when ready.</p>
          </div>
        </div>
      )}

      {/* Changes will be published warning */}
      {hasChanges && status !== 'DRAFT' && !isNew && (
        <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-400 rounded-lg flex items-center gap-3">
          <Send className="w-5 h-5 text-blue-600 shrink-0" />
          <div>
            <p className="font-medium text-blue-800">Changes Detected</p>
            <p className="text-sm text-blue-700">Contractors will be notified of these changes when you press "Publish Changes".</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* From Template selector - only for new gigs */}
        {isNew && templates.length > 0 && (
          <div className="card border-2 border-hub-200 bg-hub-50">
            <div className="card-body">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-hub-600" />
                <div className="flex-1">
                  <label className="label mb-1">Quick Fill from Template</label>
                  <select
                    onChange={(e) => handleApplyTemplate(e.target.value)}
                    className="input"
                    defaultValue=""
                  >
                    <option value="">Select a template...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-hub-600 mt-2">Fills all fields from template. You can still edit before saving.</p>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header"><h2 className="font-semibold text-gray-900">Location</h2></div>
          <div className="card-body">
            <label className="label">Select Location *</label>
            <LocationDropdown value={locationId} onChange={handleLocationChange} hierarchy={hierarchy} onAddClient={() => setShowAddClient(true)} onAddVenue={openAddVenue} onAddLocation={openAddLocation} />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="font-semibold text-gray-900 flex items-center gap-2"><MapPin className="w-4 h-4" />Address</h2></div>
          <div className="card-body">
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="input" placeholder="Venue address for contractor directions" />
            <p className="text-xs text-gray-400 mt-1">Auto-filled from Location → Venue → Client. Override if needed.</p>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="font-semibold text-gray-900">Date & Time</h2></div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className="label">Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" required /></div>
              <div><label className="label">Start Time</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input" required /></div>
              <div><label className="label">End Time</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input" required /></div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="font-semibold text-gray-900">Financials</h2></div>
          <div className="card-body space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="label">Client Billed</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span><input type="number" value={clientBilled} onChange={(e) => setClientBilled(e.target.value)} className="input pl-7" placeholder="0" min="0" step="1" required /></div></div>
              <div><label className="label">Expenses</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span><input type="number" value={expenses} onChange={(e) => setExpenses(e.target.value)} className="input pl-7" placeholder="0" min="0" step="1" /></div><p className="text-xs text-gray-400 mt-1">Travel, lodging, etc.</p></div>
            </div>
            {clientBilled && (
              <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div><p className="text-sm text-gray-500">Net</p><p className="text-lg font-semibold">{formatCurrency(net)}</p></div>
                <div><p className="text-sm text-gray-500">Total Payout</p><p className="text-lg font-semibold">{formatCurrency(totalPayout)}</p></div>
                <div><p className="text-sm text-gray-500">Production Fee</p><p className={`text-lg font-semibold ${productionFee < 0 ? 'text-red-600' : ''}`}>{formatCurrency(productionFee)}</p></div>
                <div><p className="text-sm text-gray-500">Margin</p><p className={`text-lg font-semibold margin-${getMarginStatus(marginPercent)}`}>{formatPercent(marginPercent)}</p></div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header flex items-center justify-between"><h2 className="font-semibold text-gray-900">Contractors</h2><button type="button" onClick={handleAddAssignment} className="btn-secondary btn-sm"><Plus className="w-4 h-4" />Add</button></div>
          <div className="card-body space-y-3">
            {assignments.length === 0 ? (<p className="text-gray-500 text-center py-4">No contractors assigned yet</p>) : (assignments.map((assignment, index) => (<AssignmentRow key={index} assignment={assignment} contractors={contractors} roles={roles} onChange={(updated) => handleUpdateAssignment(index, updated)} onRemove={() => handleRemoveAssignment(index)} onForceAccept={handleForceAccept} />)))}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="font-semibold text-gray-900">Notes</h2></div>
          <div className="card-body"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="input min-h-[100px]" placeholder="Any special instructions or notes..." /></div>
        </div>

        <div className="flex items-center gap-3">
          {/* Primary button: Publish Gig (for new/draft), Publish Changes (if changes), or Save Gig */}
          <button 
            type="submit" 
            className={`btn-primary ${hasChanges && status !== 'DRAFT' && !isNew ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            disabled={saving}
          >
            {getSubmitButtonIcon()}
            {getSubmitButtonText()}
          </button>
          {/* Save as Draft - only for new gigs or existing drafts */}
          {(isNew || status === 'DRAFT') && (
            <button 
              type="button" 
              onClick={(e) => handleSubmit(e, true)} 
              className="btn-secondary border-2 border-yellow-400 text-yellow-700 hover:bg-yellow-50" 
              disabled={saving}
            >
              Save as Draft
            </button>
          )}
          <button type="button" onClick={goBack} className="btn-secondary">Cancel</button>
        </div>
      </form>
      
      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={showCancelConfirm}
        title="Cancel Gig"
        message="Cancel this gig? The gig will remain on the calendar as cancelled and may still be payable. Contractors will be notified."
        confirmText="Cancel Gig"
        confirmStyle="danger"
        onConfirm={confirmCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
      
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Gig"
        message="Permanently delete this gig? This will remove it completely from the calendar and cannot be undone. No financial records will be kept."
        confirmText="Delete Permanently"
        confirmStyle="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      
      <ConfirmModal
        isOpen={showForceAcceptConfirm}
        title="Force Accept"
        message="Force accept this assignment? This will mark the contractor as accepted without their confirmation."
        confirmText="Force Accept"
        confirmStyle="default"
        onConfirm={confirmForceAccept}
        onCancel={() => { setShowForceAcceptConfirm(false); setPendingForceAcceptId(null); }}
      />
    </div>
  );
}
